import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { ChipGroup } from '@/components/app/chip-group'
import { EmptyState } from '@/components/app/empty-state'
import { ErrorBanner } from '@/components/app/error-banner'
import { Fab } from '@/components/app/fab'
import { Field, fieldClass } from '@/components/app/field'
import { ListSkeleton } from '@/components/app/list-skeleton'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { SubmitButton } from '@/components/app/submit-button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { apiPatch, apiPost, apiPut } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { getAuthUser } from '@/lib/auth'
import { MEMBER_STATUS_COLORS, MEMBER_STATUS_LABELS, MEMBER_STATUSES } from '@/lib/constants'
import { type Member, memberInitials, membersQuery } from '@/lib/queries/members'
import { canManageMembers } from '@/lib/roles'

export const Route = createFileRoute('/_staff/membres/')({
  beforeLoad: () => {
    const user = getAuthUser()
    if (!user || !canManageMembers(user.roles)) throw redirect({ to: '/accueil' })
  },
  component: MembersScreen,
})

const STATUS_OPTIONS = MEMBER_STATUSES.map((value) => ({
  value,
  label: MEMBER_STATUS_LABELS[value],
}))

function MembersScreen() {
  const [search, setSearch] = useState('')
  // L'app native met `search` directement dans la clé de requête : un appel
  // réseau par frappe.
  const debouncedSearch = useDebouncedValue(search, 300)

  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<Member | null>(null)

  const { data, isPending } = useQuery({
    ...membersQuery(debouncedSearch),
    placeholderData: (previous) => previous,
  })

  const total = data?.meta.total ?? 0

  return (
    <Screen withTabBar>
      <ScreenHeader
        title="Fidèles"
        subtitle={data ? `${total} enregistré${total > 1 ? 's' : ''}` : undefined}
      />

      <div className="flex flex-col gap-4 px-5">
        <div className="relative">
          <Search
            size={18}
            color="#8A9BB0"
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, téléphone ou numéro"
            aria-label="Rechercher un fidèle"
            className="h-13 w-full rounded-[14px] border border-navy-800 bg-navy-900 py-3 pr-4 pl-11 text-[16px] text-chalk placeholder:text-mist focus:border-gold focus:outline-none"
          />
        </div>

        {isPending && <ListSkeleton rows={6} />}

        {data?.data.length === 0 && (
          <EmptyState
            message={debouncedSearch ? 'Aucun fidèle ne correspond.' : 'Aucun fidèle enregistré.'}
          />
        )}

        <ul className="flex flex-col gap-2">
          {data?.data.map((member) => (
            <li key={member.id}>
              <button
                type="button"
                onClick={() => setSelected(member)}
                className="flex w-full items-center gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3 text-left"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-[14px] font-semibold text-gold">
                  {memberInitials(member)}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-semibold text-chalk">
                    {member.firstName} {member.lastName}
                  </span>
                  <span className="block truncate text-[12px] text-steel">
                    {member.memberNumber} · {member.department?.name ?? 'Sans département'}
                  </span>
                </span>

                <StatusBadge status={member.status} />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <Fab onClick={() => setCreateOpen(true)} label="Nouveau fidèle" />

      <CreateMemberSheet open={createOpen} onOpenChange={setCreateOpen} />
      <MemberDetailSheet member={selected} onClose={() => setSelected(null)} />
    </Screen>
  )
}

function StatusBadge({ status }: { status: string }) {
  const color = MEMBER_STATUS_COLORS[status] ?? '#8A9BB0'

  return (
    <span
      className="shrink-0 rounded-[20px] px-2.5 py-1 text-[11px] font-semibold"
      // Teinte dérivée de la couleur du statut — `22` ≈ 13 % d'opacité.
      style={{ color, backgroundColor: `${color}22` }}
    >
      {MEMBER_STATUS_LABELS[status] ?? status}
    </span>
  )
}

function CreateMemberSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [status, setStatus] = useState<string>('VISITOR')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      apiPost('members', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        // Champ optionnel : envoyer une chaîne vide ferait échouer la validation.
        phone: phone.trim() || undefined,
        status,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onOpenChange(false)
      setFirstName('')
      setLastName('')
      setPhone('')
      setStatus('VISITOR')
      setError(null)
    },
    onError: async (err) => {
      setError(getApiErrorMessage(err, "Impossible d'enregistrer le fidèle."))
    },
  })

  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (canSubmit && !mutation.isPending) mutation.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85dvh] border-navy-800 bg-navy-900">
        <SheetHeader>
          <SheetTitle className="text-chalk">Nouveau fidèle</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-y-auto px-5 pb-6">
          <Field label="Prénom">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              className={fieldClass}
            />
          </Field>

          <Field label="Nom">
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              className={fieldClass}
            />
          </Field>

          <Field label="Téléphone (facultatif)">
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+225 07 00 00 00 00"
              className={fieldClass}
            />
          </Field>

          <ChipGroup label="Statut" options={STATUS_OPTIONS} value={status} onChange={setStatus} />

          <ErrorBanner message={error} />

          <SubmitButton disabled={!canSubmit} loading={mutation.isPending}>
            Enregistrer
          </SubmitButton>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function MemberDetailSheet({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [pin, setPin] = useState('')
  const [pinFeedback, setPinFeedback] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const statusMutation = useMutation({
    // ⚠ PATCH et non PUT : le contrôleur déclare `@Patch(':id')`. L'app native
    // utilise PUT, ce qui ne correspond à aucune route — le changement de
    // statut y est donc sans effet.
    mutationFn: (status: string) => apiPatch(`members/${member?.id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
    onError: async (err) => {
      setError(getApiErrorMessage(err, 'Impossible de modifier le statut.'))
    },
  })

  const pinMutation = useMutation({
    mutationFn: () => apiPut(`members/${member?.id}/pin`, { pin }),
    onSuccess: () => {
      setPin('')
      setPinFeedback('PIN réinitialisé.')
      setError(null)
    },
    onError: async (err) => {
      setError(getApiErrorMessage(err, 'Impossible de réinitialiser le PIN.'))
    },
  })

  if (!member) return null

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          setPin('')
          setPinFeedback(null)
          setError(null)
          onClose()
        }
      }}
    >
      <SheetContent side="bottom" className="max-h-[85dvh] border-navy-800 bg-navy-900">
        <SheetHeader>
          <SheetTitle className="text-chalk">
            {member.firstName} {member.lastName}
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-5 overflow-y-auto px-5 pb-6">
          <dl className="flex flex-col gap-2 rounded-[14px] border border-navy-800 px-4 py-3">
            <InfoRow label="Numéro" value={member.memberNumber} />
            <InfoRow label="Téléphone" value={member.phone ?? '—'} />
            <InfoRow label="Département" value={member.department?.name ?? '—'} />
          </dl>

          <ChipGroup
            label="Statut"
            options={STATUS_OPTIONS}
            value={member.status}
            onChange={(status) => statusMutation.mutate(status)}
          />

          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-semibold tracking-[0.8px] text-steel uppercase">
              Réinitialiser le PIN mobile
            </span>
            <div className="flex gap-2">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 4))
                  setPinFeedback(null)
                }}
                placeholder="••••"
                aria-label="Nouveau PIN à 4 chiffres"
                className={`${fieldClass} flex-1 text-center tracking-[8px]`}
              />
              <button
                type="button"
                onClick={() => pinMutation.mutate()}
                disabled={pin.length !== 4 || pinMutation.isPending}
                className="rounded-[14px] bg-gold px-5 text-[15px] font-semibold text-navy-950 disabled:opacity-40"
              >
                Définir
              </button>
            </div>
            {pinFeedback && <p className="text-[13px] text-success">{pinFeedback}</p>}
          </div>

          <ErrorBanner message={error} />
        </div>
      </SheetContent>
    </Sheet>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[13px] text-steel">{label}</dt>
      <dd className="truncate text-[14px] text-chalk">{value}</dd>
    </div>
  )
}
